import { Box, Chip } from '@mui/material'
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AdopterDto, AnimalDto, AnimalProfileDto } from '../../../interactors/gen/backendClient'
import { AccessoriesTab } from '../../Adoptions/AccessoriesTab'
import { theme } from '../../theme'
import { AccessoriesCategory } from './AccessoriesCategory'
import { FoodCategory } from './FoodCategory'
import { WellbeingCategory } from './WellbeingCategory'
import { DealsCategory } from './DealsCategory'

export enum Categories {
  Deals = 'deals',
  Food = 'food',
  Wellbeing = 'wellbeing',
  Accessories = 'accessories',
  Fidelami = 'fidelami',
}

interface Props {
  animalProfile: AnimalProfileDto | AnimalDto
  adopter: AdopterDto
  adoptionMode?: boolean
}

export const ShoppingCategory: React.FC<Props> = ({ animalProfile, adoptionMode, adopter }) => {
  const isAdoptionMode = adoptionMode === true
  const [selectedCategory, setSelectedCategory] = React.useState<Categories>(
    isAdoptionMode ? Categories.Fidelami : Categories.Deals
  )

  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(window.location.search)
  const handleCategoryChange = (category: Categories) => {
    setSelectedCategory(category)
    navigate(
      `${location.pathname}?${
        adoptionMode
          ? `shopping=true&shoppingCategory=${category}`
          : `tab=shopping&category=recommandations&shoppingCategory=${category}`
      }`
    )
  }

  useEffect(() => {
    const tab = queryParams.get('shoppingCategory') as Categories
    if (tab) {
      setSelectedCategory(tab)
    } else {
      setSelectedCategory(isAdoptionMode ? Categories.Fidelami : Categories.Deals)
    }
  }, [queryParams, isAdoptionMode])

  const baseCategories: Categories[] = isAdoptionMode
    ? [Categories.Fidelami, Categories.Deals]
    : (Object.values(Categories) as Categories[])

  const availableCategories = baseCategories.filter((category) => {
    if (animalProfile.species !== 'cat') {
      if (animalProfile.species === 'dog') {
        return category !== Categories.Food
      }
      return category !== Categories.Food && category !== Categories.Fidelami
    }
    return true
  })

  const renderCategory = () => {
    switch (selectedCategory) {
      case Categories.Food:
        return <FoodCategory animalProfile={animalProfile} />
      case Categories.Wellbeing:
        return <WellbeingCategory animalProfile={animalProfile} />
      case Categories.Accessories:
        return <AccessoriesCategory animalProfile={animalProfile} />
      case Categories.Fidelami:
        return <AccessoriesTab animalProfile={animalProfile} alreadyAdoptedAnimal />
      case Categories.Deals:
        return <DealsCategory animal={animalProfile} adopter={adopter} />
      default:
        return <Box />
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          overflowX: 'auto',
          mx: { xs: isAdoptionMode ? -0.5 : -2, md: 0 },
          px: { xs: isAdoptionMode ? 0.5 : 1, md: 0 },
        }}
      >
        {availableCategories.map((category) => (
          <Chip
            key={category}
            label={translateCategories(category)}
            clickable
            onClick={() => handleCategoryChange(category)}
            sx={{
              backgroundColor: selectedCategory === category ? theme.palette.secondary.main : 'transparent',
              border: `1px solid ${theme.palette.secondary.main}`,
              color: selectedCategory === category ? 'white' : theme.palette.secondary.main,
              fontSize: '14px',
              px: '12px',
              py: '20px',
              borderRadius: '20px',
              fontWeight: 'bold',
              '&:focus': {
                backgroundColor: theme.palette.secondary.main,
                color: 'white',
              },
            }}
          />
        ))}
      </Box>
      {/* <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: lightBlue,
          borderRadius: '12px',
          p: 1.5,
          gap: '8px',
          width: '100%',
          border: `1px solid ${theme.palette.secondary.main}`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography
            sx={{ fontSize: '15px', fontWeight: 600 }}
          >{`10% des revenus Petso sont alloués pour soutenir les refuges et associations de protection animale.`}</Typography>
        </Box>
      </Box> */}
      {renderCategory()}
    </Box>
  )
}

function translateCategories(category: Categories): string {
  switch (category) {
    case Categories.Accessories:
      return 'Accessoires'
    case Categories.Food:
      return 'Alimentation'
    case Categories.Wellbeing:
      return 'Bien-être'
    case Categories.Fidelami:
      return 'Les kits par Fidelami'
    case Categories.Deals:
      return 'Bons plans Petso'
    default:
      return ''
  }
}
